'use client';

import React from 'react';
import clsx from 'clsx';
import Link from 'components/commercetools-ui/atoms/link';
import MarkdownHTML from 'components/commercetools-ui/organisms/markdown/indexHTML';
import MarkdownRM from 'components/commercetools-ui/organisms/markdown/indexRM';
import { trackCtaClick } from 'helpers/analytics/ga4Events';
import { getHeadingStyles, getPositionClassesForAll } from '../constant';
import { ContentBlock } from '../types';

interface HeroContentBlockProps {
  contentBlock: ContentBlock[];
  isDesktopSize: boolean;
  keyInfo: number;
  onCtaClick?: () => void;
}

export default function HeroContentBlock({
  contentBlock,
  isDesktopSize,
  keyInfo,
  onCtaClick,
}: Readonly<HeroContentBlockProps>) {
  return (
    <>
      {contentBlock?.map((block, blockIndex) => {
        // Check if block has any meaningful content
        const hasHeadline = block.headline?.some((h) => h.headlineLabel?.trim());
        const hasSubtext = block.subtext?.some((s) => {
          // Check for regular subtext
          if (s.showText && s.subtextLabel?.trim()) {
            return true;
          }
          // Check for HTML/Markdown content when showText is false
          if (!s.showText) {
            // HTML content (when useHtml is true)
            if (block.useHtml && s.subhtml?.trim()) {
              return true;
            }
            // Markdown content
            if (s.markdown?.trim()) {
              return true;
            }
          }
          return false;
        });
        const hasCTA = block.cta?.some((c) => c.label?.trim());
        const hasLegalText = block.legalText?.some((l) => {
          // Check for regular legal text
          if (l.showText && l.legalLabel?.trim()) {
            return true;
          }
          // Check for HTML/Markdown content when showText is false
          if (!l.showText) {
            // HTML content (when useHtml is true)
            if (block.useHtml && l.subhtml?.trim()) {
              return true;
            }
            // Markdown content
            if (l.markdown?.trim()) {
              return true;
            }
          }
          return false;
        });
        const hasContent = hasHeadline || hasSubtext || hasCTA || hasLegalText;

        // Don't render the block if there's no content
        if (!hasContent) {
          return null;
        }

        const blockPosition = block.position;

        return (
          <div
            id={'content-block-'.concat(blockIndex.toString())}
            key={'content-block-'.concat(blockIndex.toString())}
            className={clsx(
              'flex flex-col overflow-hidden h-auto break-words p-0 sm:p-20 md:p-36 lg:p-50',
              isDesktopSize && `absolute ${getPositionClassesForAll(blockPosition)} w-[61%] md:w-[44%] lg:w-2/5`,
              !isDesktopSize && 'relative w-full max-w-full pt-12',
            )}
            style={{ backgroundColor: isDesktopSize ? block.backgroundColor : 'none' }}
          >
            <div
              id="content-container"
              className={isDesktopSize ? clsx(
                '',
                {'origin-left': blockPosition === 'left'},
                {'origin-right': blockPosition === 'right'},
                {'origin-center': blockPosition === 'center'},
                'scale-[.65] md:scale-90 lg:scale-100'
              ) : ''}
            >
              {/* Headlines */}
              {block.headline?.map((headline, index) => (
                <h1
                  id={`banner-tile-headline-${blockIndex}-${index}`}
                  key={'headline' + index}
                  className={clsx(isDesktopSize && 'line-clamp-2 text-balance break-words', 'leading-[120%]')}
                  style={getHeadingStyles(block.headline, isDesktopSize)}
                  data-ffid={`lbl-headline-${keyInfo}`}
                >
                  {headline.headlineLabel}
                </h1>
              ))}
              {/* Subtext */}
              {block.subtext?.map((subtext, index) => (
                <React.Fragment key={'subtext' + index}>
                  {subtext.showText &&
                    (subtext.url ? (
                      <Link
                        id={`banner-tile-subtext-link-${blockIndex}-${index}`}
                        link={subtext.url}
                        className={clsx('line-clamp-5 block cursor-pointer leading-9')}
                        style={getHeadingStyles(block.subtext, isDesktopSize)}
                        data-ffid={`lnk-subtext-${keyInfo}`}
                      >
                        <h3>{subtext.subtextLabel}</h3>
                      </Link>
                    ) : (
                      <h3
                        id={`banner-tile-subtext-${blockIndex}-${index}`}
                        style={getHeadingStyles(block.subtext, isDesktopSize)}
                        className={clsx('line-clamp-5 leading-[120%]')}
                        data-ffid={`lbl-subtext-${keyInfo}`}
                      >
                        {subtext.subtextLabel}
                      </h3>
                    ))}
                </React.Fragment>
              ))}
              {block.subtext?.map((subtext, index) => (
                <React.Fragment key={'subtextFragement' + index}>
                  {!subtext.showText && (
                    <div
                      id={`banner-tile-markdown-wrapper-${blockIndex}-${index}`}
                      key={'subtext' + index}
                      className={clsx('mt-16 overflow-hidden')}
                    >
                      {subtext.url ? (
                        <Link
                          id={`banner-tile-subtext-markdown-link-${blockIndex}-${index}`}
                          link={subtext.url}
                          className="cursor-pointer"
                          data-ffid={`lnk-subtext-markdown-${blockIndex}-${index}`}
                        >
                          {block.useHtml && subtext.subhtml ? (
                            <MarkdownHTML
                              html={subtext.subhtml}
                              style={getHeadingStyles(block.subtext, isDesktopSize)}
                              className={clsx('prose-invert [&>*]:line-clamp-1')}
                            />
                          ) : (
                            <>
                              {subtext.markdown && (
                                <MarkdownRM
                                  markdown={subtext.markdown}
                                  style={getHeadingStyles(block.subtext, isDesktopSize)}
                                  className={clsx('prose-invert [&>*]:line-clamp-1')}
                                />
                              )}
                            </>
                          )}
                        </Link>
                      ) : (
                        <>
                          {block.useHtml && subtext.subhtml ? (
                            <MarkdownHTML
                              html={subtext.subhtml}
                              style={getHeadingStyles(block.subtext, isDesktopSize)}
                              className={clsx('prose-invert [&>*]:line-clamp-1')}
                            />
                          ) : (
                            <>
                              {subtext.markdown && (
                                <MarkdownRM
                                  markdown={subtext.markdown}
                                  style={getHeadingStyles(block.subtext, isDesktopSize)}
                                  className={clsx('prose-invert [&>*]:line-clamp-1')}
                                />
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))}
              {/* CTA Buttons */}
              {block.cta?.map((cta, index) => {
                const commonClasses = clsx(
                  'mt-20 line-clamp-2 w-fit rounded-full py-10 no-underline', // added no-underline for links
                  {
                    'mx-auto': !isDesktopSize && cta?.elementPosition?.toLowerCase() === 'center',
                    'ml-auto mr-0': !isDesktopSize && cta?.elementPosition?.toLowerCase() === 'right',
                    'ml-0 mr-auto': !isDesktopSize && cta?.elementPosition?.toLowerCase() === 'left',
                  },
                  isDesktopSize && cta?.ctaBorderColor && 'border-2 border-solid',
                  !isDesktopSize && cta?.ctaBorderColorMobile && 'border-2 border-solid',
                  isDesktopSize && !cta?.ctaBGColor && !cta.ctaBorderColor ? 'px-0' : 'px-28 sm:px-34',
                  !isDesktopSize && !cta?.ctaBGColorMobile && !cta.ctaBorderColorMobile ? 'px-0' : 'px-28 sm:px-34',
                  !isDesktopSize && 'mx-auto',
                );

                const focusClasses = clsx(
                  'focus-visible:outline-none',
                  'focus-visible:ring-2',
                  'focus-visible:ring-white',
                  'focus-visible:ring-offset-2',
                  'focus-visible:ring-offset-black',
                );

                const commonStyles = {
                  backgroundColor: isDesktopSize ? cta?.ctaBGColor : cta?.ctaBGColorMobile,
                  borderColor: isDesktopSize ? cta?.ctaBorderColor : cta?.ctaBorderColorMobile,
                  ...getHeadingStyles(block.cta, isDesktopSize), // Apply text styles directly to the parent wrapper
                };

                // Case 1: Active Link (URL exists)
                if (cta.url) {
                  const linkUrl = typeof cta.url === 'string' ? cta.url : ((cta.url as any)?.link ?? '');

                  return (
                    <Link
                      id={`banner-tile-cta-link-${blockIndex}-${index}`}
                      key={'cta' + index}
                      link={cta.url}
                      className={clsx(commonClasses, focusClasses, 'cursor-pointer', {
                        'inline-block': isDesktopSize,
                        'block text-center': !isDesktopSize,
                      })}
                      style={commonStyles}
                      onClick={() => {
                        trackCtaClick('hero_banner', cta.label ?? '', linkUrl);
                        onCtaClick?.();
                      }}
                      data-ffid={`lnk-cta-hero-${keyInfo}`}
                    >
                      {cta.label}
                    </Link>
                  );
                }

                // Case 2: Disabled State / Plain Text (No URL exists)
                return (
                  <button
                    id={`banner-tile-cta-button-${blockIndex}-${index}`}
                    key={'cta' + index}
                    disabled // Properly disables the button element natively
                    className={clsx(commonClasses, focusClasses, 'cursor-default opacity-75', {
                      'inline-block': isDesktopSize,
                      'block text-center': !isDesktopSize,
                    })}
                    style={commonStyles}
                    data-ffid={`btn-cta-${keyInfo}`}
                  >
                    {cta.label}
                  </button>
                );
              })}
              {/* Legal Text */}
              {block.legalText?.map((legal, index) => (
                <React.Fragment key={index + JSON.stringify(legal)}>
                  {legal.showText &&
                    (legal.url ? (
                      <Link
                        id={`banner-tile-legal-link-${blockIndex}-${index}`}
                        link={legal.url}
                        className={clsx('mb-8 mt-24 line-clamp-2 block cursor-pointer', {})}
                        style={getHeadingStyles(block.legalText, isDesktopSize)}
                        data-ffid={`lnk-legal-${keyInfo}`}
                        aria-label={`${legal?.legalLabel?.length > 0 ? 'Required option: ' : ''}${legal?.legalLabel ?? ''}`}
                      >
                        {legal?.legalLabel?.length > 0 && (
                          <span
                            className={`${isDesktopSize ? legal.fontColor : 'text-[#DB0000]'} mr-4`}
                            data-ffid={`lbl-hero-legal-mobile-asterisk-${index}`}
                            aria-hidden="true"
                          >
                            {'*'}
                          </span>
                        )}
                        {legal?.legalLabel}
                      </Link>
                    ) : (
                      <div
                        id={`banner-tile-legal-text-${blockIndex}-${index}`}
                        style={getHeadingStyles(block.legalText, isDesktopSize)}
                        className={clsx('line-clamp-2', {
                          'mt-12': legal?.legalLabel?.length > 0,
                        })}
                        data-ffid={`lbl-legal-${keyInfo}`}
                      >
                        {legal?.legalLabel?.length > 0 && (
                          <span
                            className={`${isDesktopSize ? legal.fontColor : 'text-[#DB0000]'} mr-4`}
                            data-ffid={`lbl-hero-legal-mobile-asterisk-${index}`}
                          >
                            {'*'}
                          </span>
                        )}
                        {legal?.legalLabel}
                      </div>
                    ))}
                </React.Fragment>
              ))}
              {block.legalText?.map((legal, index) => (
                <React.Fragment key={index + JSON.stringify(legal)}>
                  {!legal.showText && (
                    <div
                      id={`banner-tile-legal-markdown-wrapper-${blockIndex}-${index}`}
                      className={clsx('mb-8 mt-24 overflow-hidden', {
                        'mx-auto': !isDesktopSize && legal?.elementPosition?.toLowerCase() === 'center',
                        'ml-auto mr-0': !isDesktopSize && legal?.elementPosition?.toLowerCase() === 'right',
                        'ml-0 mr-auto': !isDesktopSize && legal?.elementPosition?.toLowerCase() === 'left',
                      })}
                    >
                      {legal.url ? (
                        <Link
                          id={`banner-tile-legal-markdown-link-${blockIndex}-${index}`}
                          link={legal.url}
                          className="cursor-pointer"
                          data-ffid={`lnk-legal-markdown-${blockIndex}-${index}`}
                        >
                          {block.useHtml && legal.subhtml ? (
                            <MarkdownHTML
                              html={legal.subhtml}
                              style={getHeadingStyles(block.legalText, isDesktopSize)}
                              className={clsx('prose-invert [&>*]:line-clamp-1', {
                                'text-center': !isDesktopSize,
                              })}
                            />
                          ) : (
                            <>
                              {legal.markdown && (
                                <MarkdownRM
                                  markdown={legal.markdown}
                                  style={getHeadingStyles(block.legalText, isDesktopSize)}
                                  className={clsx('prose-invert [&>*]:line-clamp-1', {
                                    'text-center': !isDesktopSize,
                                  })}
                                />
                              )}
                            </>
                          )}
                        </Link>
                      ) : (
                        <>
                          {block.useHtml && legal.subhtml ? (
                            <MarkdownHTML
                              html={legal.subhtml}
                              style={getHeadingStyles(block.legalText, isDesktopSize)}
                              className={clsx('prose-invert [&>*]:line-clamp-1', {
                                'text-center': !isDesktopSize,
                              })}
                            />
                          ) : (
                            <>
                              {legal.markdown && (
                                <MarkdownRM
                                  markdown={legal.markdown}
                                  style={getHeadingStyles(block.legalText, isDesktopSize)}
                                  className={clsx('prose-invert [&>*]:line-clamp-1', {
                                    'text-center': !isDesktopSize,
                                  })}
                                />
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}
